import axios from 'axios';
import './tools.js';
import './styles/theme.css';
import './App.css';
import { ZoomMtg } from '@zoom/meetingsdk';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import useCrossBrowserSpeech from './hooks/useCrossBrowserSpeech';
import io from 'socket.io-client';
import moment from 'moment';
import TranscriptPanel from './components/TranscriptPanel/TranscriptPanel';
import StatusBar from './components/StatusBar/StatusBar';

ZoomMtg.preLoadWasm();
ZoomMtg.prepareWebSDK();

/* ── Inject Sentradesk theme into Zoom SDK after it renders ── */
const SDK_KEY    = process.env.REACT_APP_SDK_KEY    || '0sxlPnb5dYJSUOGVB79uvOWKPfHvtw92Eeyo';
const SDK_SECRET = "MDa05PHJJzV8lCqCF6bjwIyhLBsxE3JYocE1";
const ENDPOINT   = process.env.REACT_APP_SERVER_URL || 'http://localhost:5001';
const ENDPOINT_LOGGER = process.env.REACT_APP_LOGGER_URL || 'http://localhost:5001';
const LEAVE_URL  = process.env.REACT_APP_LEAVE_URL || 'http://localhost:3006';
const PRE_CORRECTION_URL  = 'https://zoomrti.taurisjapan-ai.com/API/CorrectionAPI/PreCorrection';
const POST_CORRECTION_URL = 'https://zoomrti.taurisjapan-ai.com/API/CorrectionAPI/PostCorrection';

/* ── Supported languages ── */
const LANG_META = {
  'en-US': { flag: '🇺🇸', label: 'English',    short: 'EN', code: 'en'    },
  'ja-JP': { flag: '🇯🇵', label: 'Japanese',   short: 'JA', code: 'ja'    },
  'zh-CN': { flag: '🇨🇳', label: 'Chinese',    short: 'ZH', code: 'zh-CN' },
  'vi-VN': { flag: '🇻🇳', label: 'Vietnamese', short: 'VI', code: 'vi'    },
  'bn-BD': { flag: '🇧🇩', label: 'Bangla',     short: 'BN', code: 'bn'    },
};

/* ── Normalize locale → Google Translate code ── */
const normalizeLang = (lang) => {
  if (!lang) return 'en';
  const l = lang.toLowerCase();
  if (l.startsWith('ja')) return 'ja';
  if (l.startsWith('zh')) return 'zh-CN';
  if (l.startsWith('vi')) return 'vi';
  if (l.startsWith('bn')) return 'bn';
  return 'en';
};


/* ── TTS voice name map per language code ── */
const VOICE_MAP = {
  'en':    'Google US English',
  'ja':    'Google 日本語',
  'zh-CN': 'Google 普通话（中国大陆）',
  'vi':    'Google Tiếng Việt',
  'bn':    'Google বাংলা',
};

/* ── Google Translate via backend API key ── */
const GOOGLE_TRANSLATE_URL = 'https://translation.googleapis.com/language/translate/v2';
const GOOGLE_API_KEY       = 'AIzaSyBfJEwSP00U8B4Kdv3sG0BY92beRaW7Ilw';

const translateText = async (text, sourceLang, targetLang) => {
  if (!text || sourceLang === targetLang) return null;
  try {
    const res = await axios.post(`${GOOGLE_TRANSLATE_URL}?key=${GOOGLE_API_KEY}`, {
      q: text, source: sourceLang, target: targetLang, format: 'text',
    });
    return res.data?.data?.translations?.[0]?.translatedText || null;
  } catch (err) {
    console.error('Translation error:', err);
    return null;
  }
};

const getDefaultTargetLang = (src) => (src === 'ja' ? 'en' : 'ja');

/* ── Parse URL params safely ── */
const parseMeetingParams = () => {
  const raw = window.location.search;
  if (!raw) return null;
  try {
    const parts = raw.slice(1).split('?');
    const result = { meetingNumber: parts[0] || '' };
    parts.slice(1).forEach(part => {
      const [key, ...rest] = part.split('=');
      result[key] = rest.join('=').replace(/%20/g, ' ').replace(/\+/g, ' ');
    });
    return result;
  } catch {
    return null;
  }
};

/* ── Toast notification ── */
const Toast = ({ toasts }) => (
  <div className="toast-container">
    {toasts.map(t => (
      <div key={t.id} className={`toast toast--${t.type || 'info'}`}>
        <span className="toast-icon">
          {t.type === 'success' && '✓'}
          {t.type === 'error'   && '✕'}
          {t.type === 'warning' && '⚠'}
          {(!t.type || t.type === 'info') && 'ℹ'}
        </span>
        <span className="toast-msg">{t.message}</span>
      </div>
    ))}
  </div>
);

/* ── Zoom-style subtitle bar ── */
const LANG_FLAGS = { 'en': '🇺🇸', 'ja': '🇯🇵', 'zh-CN': '🇨🇳', 'vi': '🇻🇳', 'bn': '🇧🇩' };
const SubtitleBar = ({ subtitle }) => {
  if (!subtitle) return null;
  return (
    <div className="subtitle-bar">
      {subtitle.original && (
        <div className="subtitle-line subtitle-line--original">
          <span className="subtitle-flag">{LANG_FLAGS[subtitle.originalLang] || '🌐'}</span>
          <span className="subtitle-speaker">{subtitle.speaker}</span>
          <span className="subtitle-text">{subtitle.original}</span>
        </div>
      )}
      {subtitle.translated && (
        <div className="subtitle-line subtitle-line--translated">
          <span className="subtitle-flag">{LANG_FLAGS[subtitle.translatedLang] || '🌐'}</span>
          <span className="subtitle-label">Translation</span>
          <span className="subtitle-text">{subtitle.translated}</span>
        </div>
      )}
    </div>
  );
};

/* ── Synth voices setup ── */
const synth  = window.speechSynthesis;
let   voices = [];

const getVoices = () => {
  voices = synth.getVoices().sort((a, b) => a.name.localeCompare(b.name));
};
synth.onvoiceschanged = getVoices;
getVoices();

/* ── Chrome TTS keep-alive fix ──────────────────────────────────────────────
   Chrome silently pauses speechSynthesis after ~10 utterances and never
   resumes on its own. Calling synth.pause()+synth.resume() every 5 s keeps
   the internal timer alive so it never freezes.
   Also: if synth is stuck in a paused state and the queue is empty, we force
   a full cancel so the next speakText call works cleanly.
──────────────────────────────────────────────────────────────────────────── */
setInterval(() => {
  if (!synth) return;
  if (synth.speaking) {
    // Nudge Chrome to keep the current utterance going
    synth.pause();
    synth.resume();
  } else if (synth.paused) {
    // Stuck paused with nothing queued — hard reset
    synth.cancel();
  }
}, 5001);

/* ── TTS utterance queue ────────────────────────────────────────────────────
   Chrome sometimes drops speak() calls when called in quick succession.
   We serialize through a small queue instead of calling synth.speak() directly.
──────────────────────────────────────────────────────────────────────────── */
const ttsQueue  = [];
let   ttsActive = false;
let   ttsOnUtteranceDone = null; // per-utterance callback (for playback guard)

const ttsNext = () => {
  if (ttsActive || ttsQueue.length === 0) return;
  ttsActive = true;
  const { utterance, onDone } = ttsQueue.shift();
  utterance.onend = () => {
    ttsActive = false;
    if (onDone) onDone();
    // If queue is empty, fire the global callback
    if (ttsQueue.length === 0 && ttsOnUtteranceDone) ttsOnUtteranceDone();
    ttsNext();
  };
  utterance.onerror = () => {
    ttsActive = false;
    if (onDone) onDone();
    if (ttsQueue.length === 0 && ttsOnUtteranceDone) ttsOnUtteranceDone();
    ttsNext();
  };
  synth.speak(utterance);
};

const ttsSpeak = (utterance, onDone) => {
  if (ttsQueue.length >= 3) ttsQueue.shift();
  ttsQueue.push({ utterance, onDone });
  ttsNext();
};

const ttsCancelAll = () => {
  ttsQueue.length = 0;
  ttsActive = false;
  synth.cancel();
};

function App() {
  const testTool = window.testTool;

  // ── State ──
  const [name,           setName]          = useState('');
  const [language,       setLanguage]      = useState('');
  const [users,          setUsers]         = useState([]);
  const [messages,       setMessages]      = useState([]);
  const [panelOpen,      setPanelOpen]     = useState(false);
  const [listening,      setListening]     = useState(false);
  const [connected,      setConnected]     = useState(false);
  const [translating,    setTranslating]   = useState(false);
  const [toasts,         setToasts]        = useState([]);
  const [subtitle,       setSubtitle]      = useState(null);
  const subtitleTimer = useRef(null);
  const [meetingStarted, setMeetingStarted]= useState(false);
  const [joining,        setJoining]       = useState(false);

  const socketRef      = useRef(null);
  const socketLogRef   = useRef(null);
  const prevMessageRef = useRef('');
  const prevUserRef    = useRef('');  // eslint-disable-line no-unused-vars
  const recognizedRef  = useRef('');
  const msgIdCounter   = useRef(0);
  const nextMsgId = () => ++msgIdCounter.current;

  // ── Audio recording refs (record actual voice for playback) ──
  const audioRecorderRef = useRef(null);
  const audioStreamRef   = useRef(null);
  const audioChunksRef   = useRef([]);
  const latestAudioUrl   = useRef(null);

  // ── Playback guard: ignore speech recognition while audio is playing ──
  const playbackActiveRef = useRef(false);

  // ── Voice energy gate: only accept speech above a minimum volume ──
  // Filters out quiet speaker bleed from Zoom or system audio
  const voiceEnergyRef    = useRef(0);       // current mic energy level (0-255)
  const energyMonitorRef  = useRef(null);    // animation frame ID
  const analyserRef       = useRef(null);    // Web Audio AnalyserNode
  const ENERGY_THRESHOLD  = 0.5;            // minimum energy to accept speech (tune: your voice ~1.1-1.5, speaker bleed ~0.1-0.3)

  // When TTS queue finishes, clear the playback guard after a short echo delay
  // When TTS queue finishes all utterances, clear the playback guard after echo delay
  ttsOnUtteranceDone = () => {
    setTimeout(() => { playbackActiveRef.current = false; }, 500);
  };

  // ── Toast helper ──
  const addToast = useCallback((message, type = 'info', duration = 3500) => {
    const id = Date.now();
    setToasts(prev => [...prev.slice(-3), { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
  }, []);

  // Show Zoom-style subtitle bar (original + translation)
  const showSubtitle = useCallback((speaker, original, originalLang, translated, translatedLang, duration = 6000) => {
    clearTimeout(subtitleTimer.current);
    setSubtitle({ speaker, original, originalLang, translated, translatedLang });
    subtitleTimer.current = setTimeout(() => setSubtitle(null), duration);
  }, []);

  // ── TTS helper — supports EN, JA, ZH, VI, BN ──
  // Uses ttsSpeak queue to avoid Chrome's silent-freeze bug
  const speakText = useCallback((text, lang) => {
    const utterance  = new SpeechSynthesisUtterance(text);
    const voiceSpeed = parseFloat(localStorage.getItem('voiceSpeed') || '1');
    const voicePitch = parseFloat(localStorage.getItem('voicePitch') || '1');
    const voiceName  = VOICE_MAP[lang] || VOICE_MAP['en'];
    const match = voices.find(v => v.name === voiceName)
               || voices.find(v => v.lang && v.lang.toLowerCase().startsWith(lang.split('-')[0]));
    if (match) utterance.voice = match;
    utterance.lang  = lang;
    utterance.rate  = voiceSpeed;
    utterance.pitch = voicePitch;
    ttsSpeak(utterance);
  // eslint-disable-next-line
  }, []);

  // ── Init: parse URL & connect socket ──
  useEffect(() => {
    const params = parseMeetingParams();
    if (!params) return;

    const displayName = decodeURIComponent(params.name  || params['name']  || '');
    const meetingLang = decodeURIComponent(params.lang  || params['lang']  || 'en-US');
    const meetingPwd  = decodeURIComponent(params.pwd   || params['pwd']   || '');
    const meetingRole = parseInt(params.role || params['role'] || '0', 10);
    const meetingNum  = params.meetingNumber;

    window.display_name   = displayName;
    window.meeting_lang   = meetingLang;
    window.meeting_pwd    = meetingPwd;
    window.meeting_role   = meetingRole;
    window.meeting_number = meetingNum;
    // Check both localStorage keys
    window.meeting_email  = localStorage.getItem('email') || localStorage.getItem('hostEmail') || 'rukucse11@gmail.com';

    if (!displayName) { window.location.reload(); return; }

    setName(displayName);
    setLanguage(meetingLang);

    console.log('[Socket] Connecting to:', ENDPOINT);
    const socket    = io(ENDPOINT, {
      transports: ['websocket', 'polling'],  // prefer websocket
      reconnectionAttempts: 10,
      timeout: 10000,
    });
    const socketLog = io(ENDPOINT_LOGGER, {
      transports: ['websocket', 'polling'],
    });
    socketRef.current    = socket;
    socketLogRef.current = socketLog;

    const joinData = { name: displayName, room: 'development', language: meetingLang };

    // Debug all socket events
    socket.io.on('error', (err) => console.error('[Socket] Manager error:', err));
    socket.on('connect_error', (err) => console.error('[Socket] Connect error:', err.message));
    socket.on('connect', () => {
      console.log('[Socket] Connected! id:', socket.id, 'transport:', socket.io.engine.transport.name);
      setConnected(true);
      socket.emit('join', joinData, () => {
        console.log('[Socket] Join acknowledged');
      });
    });
    socket.on('disconnect', (reason) => {
      console.log('[Socket] Disconnected, reason:', reason);
      setConnected(false);
    });

    socketLog.on('connect', () => {
      socketLog.emit('join', { name: `${displayName}_logger`, room: 'development', language: meetingLang }, () => {});
    });

    window.onbeforeunload = () => {
      socket.disconnect();
      socketLog.disconnect();
    };

    return () => { socket.off(); socketLog.off(); };
  }, []);

  // ── Speech recognition — works in ALL browsers ──
  const {
    finalTranscript,
    interimTranscript,
    resetTranscript,
    startListening,
    stopListening,
    isNative,
  } = useCrossBrowserSpeech();

  useEffect(() => {
    if (!finalTranscript) return;
    if (!socketRef.current) return;

    // Ignore speech picked up while audio is playing (TTS, recordings, system sound)
    if (playbackActiveRef.current) {
      resetTranscript();
      return;
    }

    // Voice energy gate: reject if mic energy is too low (speaker bleed, not direct speech)
    if (voiceEnergyRef.current < ENERGY_THRESHOLD) {
      console.log('[Voice Gate] Rejected — energy too low:', voiceEnergyRef.current.toFixed(1));
      resetTranscript();
      return;
    }

    recognizedRef.current = finalTranscript;

    const myLangCode = normalizeLang(window.meeting_lang);

    // Capture recorded voice audio, then process the transcript
    captureAudioBlob().then(audioUrl => {
      latestAudioUrl.current = audioUrl;

      setTranslating(true);
      axios.get(`${PRE_CORRECTION_URL}?PreText=${encodeURIComponent(finalTranscript)}`)
        .then(res => {
          const corrected = JSON.stringify(res.data).replace(/"/g, '');
          const entry = {
            id:       nextMsgId(),
            user:     window.display_name,
            text:     corrected,
            language: myLangCode,
            time:     moment().format('hh:mm:ss'),
            canPlay:  false,       // original uses recorded audio, not TTS
            audioUrl: audioUrl,    // actual recorded voice
          };
          setMessages(prev => [...prev, entry]);
        // Show original in subtitle bar immediately
        showSubtitle(window.display_name, corrected, myLangCode, null, null, 8000);

        // Translate on frontend, then send both original + translated to server
        const targetLang = getDefaultTargetLang(myLangCode);
        console.log(`[SELF] Original: "${corrected}" | Lang:${myLangCode} | TargetLang:${targetLang}`);
        translateText(corrected, myLangCode, targetLang)
          .then(translated => {
            console.log(`[SELF] Translated: "${translated}" | Sending to server...`);
            // Send original + translated to server for logging & transmitting
            socketRef.current.emit('sendMessage', {
              text:           corrected,
              language:       window.meeting_lang,
              translated:     translated || '',
              targetLanguage: targetLang,
            }, () => {});

            if (translated) {
              setMessages(prev => [...prev, {
                id:       nextMsgId(),
                user:     window.display_name,
                text:     translated,
                language: targetLang,
                time:     moment().format('hh:mm:ss'),
                canPlay:  true,
                isTranslation: true,
              }]);
              showSubtitle(window.display_name, corrected, myLangCode, translated, targetLang, 8000);
            }
          })
          .catch(() => {
            // Translation failed — still send original to server
            socketRef.current.emit('sendMessage', {
              text:       corrected,
              language:   window.meeting_lang,
              translated: '',
              targetLanguage: '',
            }, () => {});
          });

        if (socketLogRef.current) socketLogRef.current.emit('sendMessage',
          `${window.display_name}: Sent: ${corrected} at ${moment().format('YYYY-MM-DD HH:mm:ss')}`, () => {});
      })
      .catch(err => {
        console.error('Pre-correction error:', err);
        socketRef.current.emit('sendMessage', {
          text:       finalTranscript,
          language:   window.meeting_lang,
          translated: '',
          targetLanguage: '',
        }, () => {});
      })
      .finally(() => {
        setTranslating(false);
        recognizedRef.current = '';
        resetTranscript();
      });
    }); // end captureAudioBlob().then()
  // eslint-disable-next-line
  }, [finalTranscript]);

  // ── Show interim in ref only ──
  useEffect(() => {
    if (interimTranscript) {
      recognizedRef.current = interimTranscript;
    }
  }, [interimTranscript]);

  // ── Incoming message handler ──
  useEffect(() => {
    if (!socketRef.current) return;

    const handleMessage = (message) => {
      console.log('[INCOMING]', JSON.stringify(message));
      if (message.text?.includes('welcome to the room')) return;
      if (message.text?.includes('has joined')) return;
      if (prevMessageRef.current === message.text) return;
      if (recognizedRef.current === message.text) return;
      if (!message.text || message.user == null) return;
      if (message.language === 'system') return;

      // Skip own messages (we handle our own speech client-side)
      if (message.user?.toLowerCase() === window.display_name?.toLowerCase()) return;

      const myLangCode = normalizeLang(window.meeting_lang);
      const msgLang = message.language || myLangCode;

      prevMessageRef.current = message.text;

      // Show the received text (already translated to my language by server)
      const correctedText = message.text;
      const entry = {
        id:       nextMsgId(),
        user:     message.user,
        text:     correctedText,
        language: msgLang,
        time:     moment().format('hh:mm:ss'),
        canPlay:  true,
      };
      setMessages(prev => [...prev, entry]);

      // Show snackbar subtitle
      showSubtitle(message.user, correctedText, msgLang, null, null, 6000);

      // TTS playback for other users' messages (if in my language)
      if (msgLang === myLangCode && listening) {
        playbackActiveRef.current = true;
        speakText(correctedText, msgLang);
      }

      // Translate received message on frontend if it's a different language
      if (msgLang !== myLangCode) {
        translateText(correctedText, msgLang, myLangCode)
          .then(translated => {
            if (translated) {
              setMessages(prev => [...prev, {
                id:       nextMsgId(),
                user:     message.user,
                text:     translated,
                language: myLangCode,
                time:     moment().format('hh:mm:ss'),
                canPlay:  true,
                isTranslation: true,
              }]);
              showSubtitle(message.user, correctedText, msgLang, translated, myLangCode, 8000);
              if (listening) {
                playbackActiveRef.current = true;
                speakText(translated, myLangCode);
              }
            }
          })
          .catch(err => console.error('Incoming translation error:', err));
      }

      if (socketLogRef.current) socketLogRef.current.emit('sendMessage',
        `${window.display_name}: Received from ${message.user}: ${correctedText}`, () => {});
    };

    const handleRoomData = ({ users }) => setUsers(users);

    // Server sends back the sender's own message translated to the paired language
    const handleSelfTranslation = (message) => {
      if (!message.text) return;
      setMessages(prev => [...prev, {
        id:       nextMsgId(),
        user:     message.user,
        text:     message.text,
        language: message.language,
        time:     moment().format('hh:mm:ss'),
        canPlay:  true,
        isTranslation: true,
      }]);
      showSubtitle(message.user, message.text, message.language, null, null, 8000);
    };

    socketRef.current.on('message',         handleMessage);
    socketRef.current.on('roomData',        handleRoomData);
    socketRef.current.on('selfTranslation', handleSelfTranslation);

    return () => {
      if (socketRef.current) socketRef.current.off('message',         handleMessage);
      if (socketRef.current) socketRef.current.off('roomData',        handleRoomData);
      if (socketRef.current) socketRef.current.off('selfTranslation', handleSelfTranslation);
    };
  // eslint-disable-next-line
  }, [listening, speakText, addToast]);

  // ── Clear transcript event ──
  useEffect(() => {
    const handler = () => setMessages([]);
    window.addEventListener('clearTranscript', handler);
    return () => window.removeEventListener('clearTranscript', handler);
  }, []);

  // ── Auto-join on load if meeting number is already in URL ──
  useEffect(() => {
    // Small delay to ensure window.meeting_number is set by the init useEffect above
    const timer = setTimeout(() => {
      if (window.meeting_number && !meetingStarted) {
        joinMeeting();
      }
    }, 500);
    return () => clearTimeout(timer);
  // eslint-disable-next-line
  }, []);

  // ── Audio recording helpers (record actual voice for playback) ──
  const bestMimeType = useRef('');

  const createRecorder = useCallback((stream) => {
    if (!bestMimeType.current) {
      bestMimeType.current = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg', 'audio/mp4']
        .find(t => MediaRecorder.isTypeSupported(t)) || '';
    }
    const opts = bestMimeType.current ? { mimeType: bestMimeType.current } : {};
    const recorder = new MediaRecorder(stream, opts);
    audioRecorderRef.current = recorder;
    audioChunksRef.current = [];
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
    };
    recorder.start(500);
    return recorder;
  }, []);

  const startAudioRecording = useCallback(async () => {
    try {
      const rawStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          noiseSuppression: true,
          echoCancellation: true,
          autoGainControl: true,
        },
      });

      // Apply Web Audio API high-pass filter to cut low-frequency noise (fans, AC hum)
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioCtx.createMediaStreamSource(rawStream);

      // High-pass filter: removes frequencies below 85Hz (background rumble)
      const highPass = audioCtx.createBiquadFilter();
      highPass.type = 'highpass';
      highPass.frequency.value = 85;

      // Low-pass filter: removes frequencies above 8000Hz (high-pitched noise)
      const lowPass = audioCtx.createBiquadFilter();
      lowPass.type = 'lowpass';
      lowPass.frequency.value = 8000;

      // Voice energy analyser — measures mic volume to filter speaker bleed
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.3;
      analyserRef.current = analyser;

      const destination = audioCtx.createMediaStreamDestination();
      source.connect(highPass).connect(lowPass).connect(analyser).connect(destination);

      // Continuously monitor voice energy level
      const dataArray = new Uint8Array(analyser.fftSize);
      const monitorEnergy = () => {
        analyser.getByteTimeDomainData(dataArray);
        // Calculate RMS energy (root mean square of waveform amplitude)
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          const val = (dataArray[i] - 128) / 128; // normalize to -1..1
          sum += val * val;
        }
        voiceEnergyRef.current = Math.sqrt(sum / dataArray.length) * 255;
        energyMonitorRef.current = requestAnimationFrame(monitorEnergy);
      };
      monitorEnergy();

      audioStreamRef.current = destination.stream;
      // Keep raw stream ref so we can stop tracks later
      audioStreamRef._rawStream = rawStream;
      audioStreamRef._audioCtx = audioCtx;

      createRecorder(destination.stream);
    } catch (err) {
      console.error('Audio recording error:', err);
    }
  }, [createRecorder]);

  const stopAudioRecording = useCallback(() => {
    // Stop energy monitor
    if (energyMonitorRef.current) {
      cancelAnimationFrame(energyMonitorRef.current);
      energyMonitorRef.current = null;
    }
    voiceEnergyRef.current = 0;
    analyserRef.current = null;

    if (audioRecorderRef.current && audioRecorderRef.current.state !== 'inactive') {
      audioRecorderRef.current.stop();
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(t => t.stop());
      audioStreamRef.current = null;
    }
    // Clean up raw mic stream and AudioContext
    if (audioStreamRef._rawStream) {
      audioStreamRef._rawStream.getTracks().forEach(t => t.stop());
      audioStreamRef._rawStream = null;
    }
    if (audioStreamRef._audioCtx) {
      audioStreamRef._audioCtx.close().catch(() => {});
      audioStreamRef._audioCtx = null;
    }
  }, []);

  const captureAudioBlob = useCallback(() => {
    // Capture current recording as a playable blob, then start fresh recorder
    return new Promise((resolve) => {
      const recorder = audioRecorderRef.current;
      if (!recorder || recorder.state === 'inactive') {
        resolve(null);
        return;
      }

      // When recorder stops, it fires one final ondataavailable + onstop
      recorder.onstop = () => {
        const chunks = audioChunksRef.current;
        if (chunks.length === 0) { resolve(null); return; }

        const mimeType = recorder.mimeType || 'audio/webm';
        const blob = new Blob(chunks, { type: mimeType });
        audioChunksRef.current = [];

        if (blob.size < 200) { resolve(null); return; }

        const url = URL.createObjectURL(blob);

        // Start fresh recorder on same stream (new WebM container with headers)
        if (audioStreamRef.current && audioStreamRef.current.active) {
          createRecorder(audioStreamRef.current);
        }

        resolve(url);
      };

      // Stop triggers final data flush + onstop
      recorder.stop();
    });
  }, [createRecorder]);

  // ── Toggle speech recognition — works on all browsers via useCrossBrowserSpeech ──
  const toggleListening = () => {
    if (!listening) {
      startListening({ continuous: true, language: window.meeting_lang });
      startAudioRecording();
      setListening(true);
      addToast(
        isNative
          ? 'Speech recognition started. Speak clearly.'
          : 'Microphone active (server transcription mode).',
        'success'
      );
      if (socketLogRef.current) socketLogRef.current.emit('sendMessage',
        `${window.display_name}: Speech Recognition Started (${isNative ? 'native' : 'fallback'})`, () => {});
    } else {
      stopListening();
      stopAudioRecording();
      setListening(false);
      addToast('Speech recognition stopped.', 'warning');
      if (socketLogRef.current) socketLogRef.current.emit('sendMessage',
        `${window.display_name}: Speech Recognition Stopped`, () => {});
    }
  };

  // ── Play audio manually — supports recorded audio or TTS ──
  // Pauses speech recognition while audio plays to prevent mic picking up output
  // TTS goes through the queue to avoid Chrome's 10-utterance freeze bug
  const handlePlayAudio = (text, lang, audioUrl, onDone) => {
    playbackActiveRef.current = true;

    const done = () => {
      setTimeout(() => { playbackActiveRef.current = false; }, 500);
      onDone && onDone();
    };

    if (audioUrl) {
      const audio = new Audio(audioUrl);
      audio.onended = done;
      audio.onerror = done;
      audio.play().catch(done);
    } else {
      // Cancel any pending TTS, then queue the new one
      ttsCancelAll();
      const utterance = new SpeechSynthesisUtterance(text);
      const voiceName = VOICE_MAP[lang] || VOICE_MAP['en'];
      const match = voices.find(v => v.name === voiceName)
                 || voices.find(v => v.lang && v.lang.toLowerCase().startsWith(lang.split('-')[0]));
      if (match) utterance.voice = match;
      utterance.lang  = lang;
      utterance.rate  = parseFloat(localStorage.getItem('voiceSpeed') || '1');
      utterance.pitch = parseFloat(localStorage.getItem('voicePitch') || '1');
      ttsSpeak(utterance, done);
    }
  };

  // ── Inject controls into Zoom footer after join ──
  const additionalElements = () => {
    const zoomElement = document.querySelector('#foot-bar .footer__btns-container');
    if (!zoomElement) return;
    document.getElementById('root').appendChild(document.getElementById('zmmtg-root'));
    zoomElement.appendChild(document.getElementById('meeting-controls'));
  };

  // ── Start Zoom meeting ──
  const startZoomMeeting = (signature, meetingNum) => {
    const mn = meetingNum || window.meeting_number;

    // Hide our card, show Zoom SDK immediately
    const premeet = document.getElementById('premeet-main');
    if (premeet) premeet.style.display = 'none';
    document.getElementById('zmmtg-root').style.display = 'block';

    ZoomMtg.init({
      leaveUrl: LEAVE_URL,
      patchJsMedia: true,
      success: () => {
        ZoomMtg.join({
          signature,
          meetingNumber: mn,
          userName:  window.display_name,
          sdkKey:    SDK_KEY,
          userEmail: window.meeting_email,
          passWord:  window.meeting_pwd,
          tk: '',
          success: () => {
            additionalElements();
            document.getElementById('meeting-controls').style.display = 'flex';
            setJoining(false);
            addToast('Joined meeting successfully!', 'success');
          },
          error: (err) => {
            setJoining(false);
            if (premeet) premeet.style.display = 'flex';
            document.getElementById('zmmtg-root').style.display = 'none';
            addToast('Failed to join: ' + (err?.errorCode || err?.reason || 'unknown error'), 'error');
            console.error('Join error:', err);
          }
        });
      },
      error: (err) => {
        setJoining(false);
        if (premeet) premeet.style.display = 'flex';
        document.getElementById('zmmtg-root').style.display = 'none';
        addToast('Meeting SDK init failed', 'error');
        console.error('Init error:', err);
      }
    });
  };

  // ── Generate signature client-side using HMAC-SHA256 ──
  const generateSignature = async (meetingNumber, role) => {
    const iat = Math.round(new Date().getTime() / 1000) - 30;
    const exp = iat + 60 * 60 * 2;

    const header  = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
    const payload = btoa(JSON.stringify({
      sdkKey: SDK_KEY,
      appKey: SDK_KEY,
      mn:     meetingNumber,
      role:   parseInt(role, 10),
      iat,
      exp,
      tokenExp: exp,
    }));

    const msgUint8  = new TextEncoder().encode(`${header}.${payload}`);
    const keyUint8  = new TextEncoder().encode(SDK_SECRET);
    const cryptoKey = await window.crypto.subtle.importKey(
      'raw', keyUint8, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']
    );
    const signBuffer = await window.crypto.subtle.sign('HMAC', cryptoKey, msgUint8);
    const signArray  = Array.from(new Uint8Array(signBuffer));
    const signature  = btoa(String.fromCharCode(...signArray))
      .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

    return `${header}.${payload}.${signature}`;
  };

  // ── Join meeting ──
  const joinMeeting = async () => {
    if (joining || meetingStarted) return;
    setJoining(true);
    try {
      const meetingNum = window.meeting_number;
      if (!meetingNum) {
        setJoining(false);
        addToast('Meeting number not found. Check the meeting link.', 'error');
        return;
      }
      const signature = await generateSignature(meetingNum, window.meeting_role ?? 0);
      setMeetingStarted(true);
      startZoomMeeting(signature, meetingNum);
    } catch (err) {
      setJoining(false);
      addToast('Could not start meeting: ' + err.message, 'error');
      console.error(err);
    }
  };

  // ── Draggable sidebar ──
  const [sidebarPos, setSidebarPos] = useState({ x: 0, y: 0 });
  const draggingRef = useRef(false);
  const dragOffset  = useRef({ x: 0, y: 0 });

  const onDragStart = useCallback((e) => {
    draggingRef.current = true;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    dragOffset.current = { x: clientX - sidebarPos.x, y: clientY - sidebarPos.y };
    e.preventDefault();
  }, [sidebarPos]);

  useEffect(() => {
    const onDragMove = (e) => {
      if (!draggingRef.current) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      setSidebarPos({ x: clientX - dragOffset.current.x, y: clientY - dragOffset.current.y });
    };
    const onDragEnd = () => { draggingRef.current = false; };
    window.addEventListener('mousemove', onDragMove);
    window.addEventListener('mouseup', onDragEnd);
    window.addEventListener('touchmove', onDragMove, { passive: false });
    window.addEventListener('touchend', onDragEnd);
    return () => {
      window.removeEventListener('mousemove', onDragMove);
      window.removeEventListener('mouseup', onDragEnd);
      window.removeEventListener('touchmove', onDragMove);
      window.removeEventListener('touchend', onDragEnd);
    };
  }, []);

  // ── Language badge: shows flag + short code for current user ──
  const getLangBadge = (lang) => {
    const meta = LANG_META[lang];
    if (meta) return `${meta.flag} ${meta.short}`;
    return lang?.split('-')[0]?.toUpperCase() || 'EN';
  };

  // ── Pre-meeting language description ──
  const getPremeetLangText = (lang) => {
    const meta = LANG_META[lang];
    if (!meta) return `Speaking ${lang}`;
    return `${meta.flag} Speaking ${meta.label} → others hear their language`;
  };

  // No browser block — all browsers supported via MediaRecorder fallback

  return (
    <div className="App">
      {/* Zoom SDK root — controlled by SDK */}
      <div id="zmmtg-root" style={{ display: 'none' }}/>

      {/* Meeting Controls overlay */}
      <div id="meeting-controls" className="meeting-controls" style={{ display: 'none', transform: `translate(${sidebarPos.x}px, ${sidebarPos.y}px)` }}>
        {/* Drag handle */}
        <div className="sidebar-drag-handle" onMouseDown={onDragStart} onTouchStart={onDragStart}>
          <span>⋮⋮</span>
        </div>

        {/* Control Buttons */}
        <div className="controls-bar">

          {/* Brand logo */}
          <div className="ctrl-logo">
            <img src="/sentradesk_logo.png" alt="Sentradesk" style={{ height: '20px', objectFit: 'contain' }} />
          </div>

          {/* Interpretation toggle */}
          <button
            className={`ctrl-btn ctrl-btn--interpret ${listening ? 'ctrl-btn--active' : ''}`}
            onClick={toggleListening}
            title={listening ? 'Stop interpretation' : 'Start interpretation'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/>
              <path d="M19 10v2a7 7 0 01-14 0v-2"/>
              <line x1="12" y1="19" x2="12" y2="23"/>
              <line x1="8"  y1="23" x2="16" y2="23"/>
            </svg>
            <span>{listening ? 'Interpretation ON' : 'Start Interpretation'}</span>
            {listening && <span className="ctrl-live-dot"/>}
          </button>

          {/* Transcript toggle */}
          <button
            className={`ctrl-btn ctrl-btn--transcript ${panelOpen ? 'ctrl-btn--active' : ''}`}
            onClick={() => setPanelOpen(p => !p)}
            title="Toggle transcript panel"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
            </svg>
            <span>Transcript</span>
            {messages.length > 0 && <span className="ctrl-badge">{messages.length}</span>}
          </button>

          {/* Language badge — shows current user's language dynamically */}
          <div className="ctrl-lang-badge">
            {getLangBadge(language)}
          </div>

          {/* User name */}
          <div className="ctrl-user">
            <div className="ctrl-user-avatar">{name[0]?.toUpperCase() || '?'}</div>
            <span className="ctrl-user-name">{name}</span>
          </div>
        </div>

        {/* Status bar */}
        <StatusBar
          connected={connected}
          listening={listening}
          translating={translating}
          language={language}
          users={users.length}
          msgCount={messages.length}
        />
      </div>

      {/* Transcript Panel */}
      <TranscriptPanel
        isOpen={panelOpen}
        onClose={() => setPanelOpen(false)}
        selfName={name}
        messages={messages}
        onPlayAudio={handlePlayAudio}
      />

      {/* Zoom-style subtitle bar */}
      <SubtitleBar subtitle={subtitle}/>

      {/* Toast notifications */}
      <Toast toasts={toasts}/>

      {/* Pre-meeting join screen */}
      <main className="premeet-main" id="premeet-main">
        <div className="premeet-card">
          <div className="premeet-logo">
            <img src="/sentradesk_logo.png" alt="Sentradesk" style={{ height: '52px', objectFit: 'contain' }} />
          </div>
          <h1 className="premeet-title">AI Interpretation</h1>
          <p className="premeet-sub">Sentradesk</p>
          {name && <div className="premeet-name">Welcome, <strong>{name}</strong></div>}
          <button className={`premeet-btn${joining ? ' premeet-btn--loading' : ''}`} onClick={joinMeeting} disabled={joining}>
            {joining ? (
              <>
                <span className="premeet-spinner"/>
                Connecting…
              </>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M15 10l5 2-5 2V10z"/><path d="M4 8h9a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4a2 2 0 012-2z"/></svg>
                Join Meeting
              </>
            )}
          </button>
          <div className="premeet-lang">
            {getPremeetLangText(language)}
          </div>
        </div>
      </main>
    </div>
  );
}

export default App;